<template>
  <div class="rs-search-box">
    <div class="rs-search-box__container" />
    <div
      v-click-outside="handleBlur"
      class="rs-search-box__wrapper"
    >
      <RsFormInput
        :id="id"
        v-model="searchValue"
        :disabled="disabled"
        :label="label"
        :debounce="debounce"
        type="text"
        class="rs-search-input mb-0"
        :class="{ open: isSearching }"
        :placeholder="placeholder"
        :icon="optionIcon"
        :autocomplete="'off'"
        :required="required"
        :rules="rules"
        @focus="handleFocus"
      />
      <div
        class="shadow rs-search-results list-group"
        :class="{ open: isSearching }"
      >
        <a
          v-for="(o, index) in filteredOptions"
          :key="`${index}_${o.value}`"
          class="list-group-item list-group-action border-0"
          @click="handleOptionSelect(o)"
        >
          <div class="d-flex">
            <div>
              <i
                v-show="optionIcon"
                :class="`icon far fa-${optionIcon}`"
              />
            </div>
            <div class="flex-grow-1">
              {{ o.text }}
            </div>
          </div>
        </a>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { ISelectOption } from '@/shared';

export default defineComponent({
  name: 'RsFormSearchBox',

  props: {
    id: {
      type: String,
      default: 'rsFormSearchBox',
    },
    required: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Object,
      default: () => ({}),
    },
    label: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array as PropType<ISelectOption[]>,
      default: () => [],
    },
    value: {
      type: [String, Number, Object],
    },
    placeholder: {
      type: String,
      default: '',
    },
    optionIcon: {
      type: String,
      default: 'search',
    },
    debounce: {
      type: Number,
      default: 100,
    },
    /**
     * Set to true to prevent resetting of value when input field is on focus
     */
    disableFocus: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      searchValue:
        this.options.find((o: ISelectOption) => o.value === this.value)?.text
        || '',
      isSearching: false,
    };
  },

  computed: {
    filteredOptions(): ISelectOption[] {
      return this.options.filter((o: ISelectOption) => {
        const normalizedText = o.text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const normalizedSearch = this.searchValue.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        return normalizedText.toLowerCase().includes(normalizedSearch.toLowerCase());
      });
    },
  },

  watch: {
    value(): void {
      this.searchValue = this.options.find((o: ISelectOption) => o.value === this.value)?.text
        || '';
    },

    searchValue(): void {
      if (
        !this.options.find(
          (o: ISelectOption) => o.text.toLowerCase() === this.searchValue.toLowerCase(),
        )
      ) {
        this.isSearching = true;
        this.$emit('on-search', this.searchValue);
      }

      if (!this.searchValue) {
        this.isSearching = false;
      }
    },
  },

  methods: {
    handleOptionSelect(option: ISelectOption): void {
      this.searchValue = option.text;
      this.isSearching = false;
      this.$emit('input', option.value);
    },

    handleBlur(data: Event): void {
      this.isSearching = false;

      if (this.value) {
        this.searchValue = this.options.find(
          (o: ISelectOption) => o.value === this.value,
        )?.text || '';
      }

      this.$emit('blur', data);
    },

    handleFocus(data: Event): void {
      if (this.disableFocus) {
        return;
      }
      this.searchValue = '';
      this.$emit('focus', data);
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

.rs-search-box {
  position: relative;

  a {
    cursor: pointer;
    color: $dark-gray;

    .icon {
      color: $tertiary-muted;
      margin-right: 1rem;
    }

    &:hover {
      background: #eee;
      color: $dark-gray;
    }
  }

  &__container {
    height: 70px;
  }

  &__wrapper {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;

    .rs-search-input.open {
      input[type='text'],
      .input-group-text {
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;
      }
    }

    .rs-search-results {
      border-bottom-left-radius: 5px;
      border-bottom-right-radius: 5px;
      border-top-right-radius: 0;
      border-top-left-radius: 0;
      z-index: 100;
      overflow: hidden;
      transition: all ease 200ms;
      display: none;

      &.open {
        display: block;
        height: auto;
        max-height: 200px;
        overflow: auto;
      }
    }
  }

}
</style>
